import { filter } from "@msuite/utils/array";
import {
	createDocumentStructure,
	type DocumentStructureSortBy,
	type DocumentStructureSortDirection,
} from "@msuite/utils/documents";
import type {
	DocumentStructure,
	DocumentStructureItem,
	Nullable,
	Tables,
	UploadResult,
} from "@msuite/utils/types";
import { useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { BlankSlate } from "../../blank-slate";
import { useFileUpload } from "../../file-upload";
import { useLoading } from "../../hooks";
import { useTranslation } from "../../localization";
import type { RefetchFn } from "../../query";
import { toast } from "../../toast";
import type { UseRemoteFileUrl } from "../../types";
import { FileExplorerContext } from "./context";
import { FileExplorerHeader } from "./file-explorer-header";
import { FileExplorerItemRow } from "./file-explorer-item-row";
import { FileExplorerMenu } from "./file-explorer-menu";
import { FileExplorerSearch } from "./file-explorer-search";
import { FileExplorerWrapper } from "./file-explorer-wrapper";
import {
	type FileExplorerMenuExtension,
	type FileExplorerSelectColumn,
	flattenItems,
} from "./shared";

/** Props Interface */
interface FileExplorerProps<T extends { file: Tables<"files"> }> {
	uploadFn: (body: FormData) => Promise<UploadResult[] | null>;
	handleOnInsertFilesCompleted: (uploadResults: UploadResult[]) => void | Promise<void>;
	structure: Nullable<DocumentStructure<T>>;
	refetch: RefetchFn;
	isLoading: boolean;
	useRemoteFileUrl: UseRemoteFileUrl;
	onDeleteFiles: (fileIds: string[]) => void | Promise<void>;
	onDownloadFiles: (fileIds: string[]) => void | Promise<void>;
	handleOnFolderDrop: (newPath: string, fileIds: string[]) => void | Promise<void>;
	menu?: FileExplorerMenuExtension;
	/**
	 * Extra columns of select boxes, one per editable field the rows carry. Each column says where
	 * its value comes from and what to do with a new one, so the explorer renders them without
	 * knowing what the collection behind them stores.
	 */
	selectColumns?: FileExplorerSelectColumn<T>[];
	/**
	 * What the caller is allowed to do with the files in front of them. The caller decides — a
	 * file's writes follow whatever it hangs on, which this package does not know — and correcting
	 * and removing are asked per file so a caller whose answer differs between rows can say so. All
	 * three default to permissive, so an explorer that says nothing behaves as it always has.
	 */
	canUpload?: boolean;
	canUpdateFile?: (file: Tables<"files">) => boolean;
	canDeleteFile?: (file: Tables<"files">) => boolean;
}

export function FileExplorer<T extends { file: Tables<"files"> }>({
	uploadFn,
	handleOnInsertFilesCompleted,
	structure,
	refetch,
	isLoading,
	useRemoteFileUrl,
	onDeleteFiles,
	onDownloadFiles,
	handleOnFolderDrop,
	menu,
	selectColumns,
	canUpload = true,
	canUpdateFile = () => true,
	canDeleteFile = () => true,
}: FileExplorerProps<T>) {
	/** State */
	const [searchValue, setSearchValue] = useState<string>("");
	const [focusedItem, setFocusedItem] = useState<DocumentStructureItem<T> | null>(null);
	const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
	const [sortBy, setSortBy] = useState<DocumentStructureSortBy>("name");
	const [sortByDirection, setSortByDirection] = useState<DocumentStructureSortDirection>("asc");

	/** Hooks */
	const uploadLoading = useLoading();
	const { t } = useTranslation();
	const dropzone = useDropzone({
		noClick: true,
		noKeyboard: true,
		onDrop(acceptedFiles: File[]) {
			if (!canUpload) return;
			handleOnInsertFiles(acceptedFiles);
		},
	});
	const fileUploadController = useFileUpload({
		uploadFn,
	});

	/** Functions */
	async function handleOnInsertFiles(files: File[]) {
		try {
			if (files.length === 0) return;
			uploadLoading.start();
			const uploadResults = await fileUploadController.handleOnFileUpload(files);
			await handleOnInsertFilesCompleted(uploadResults);
			fileUploadController.handleOnReset();
			await refetch();
			toast.success(t("file_upload.uploaded"));
		} catch {
			toast.error(t("general.something_went_wrong"));
		} finally {
			uploadLoading.stop();
		}
	}

	async function handleOnAfterFileAction() {
		setFocusedItem(null);
		setSelectedFiles([]);
		await refetch();
	}

	// Built from the unfiltered tree on purpose: it is what the batch guards resolve a selection
	// against, and a selection survives the search box that hides half of it.
	const filesById = useMemo(() => {
		const entries = flattenItems(structure?.items ?? []).map(
			(item) => [item.file.id, item.file] as const,
		);
		return new Map<string, Tables<"files">>(entries);
	}, [structure]);

	const filteredStructure = useMemo(() => {
		if (!structure?.items) return createDocumentStructure<T>([]);
		const flatItems = flattenItems(structure?.items || []);
		if (!searchValue) {
			return createDocumentStructure(flatItems, sortBy, sortByDirection);
		}
		const filteredItems = filter(flatItems, searchValue);
		return createDocumentStructure(filteredItems, sortBy, sortByDirection);
	}, [structure, searchValue, sortBy, sortByDirection]) as DocumentStructure<T>;

	/** Render */
	return (
		<FileExplorerContext.Provider
			value={{
				setSearchValue,
				focusedItem,
				setFocusedItem,
				useRemoteFileUrl,
				selectedFiles,
				setSelectedFiles,
				structure: filteredStructure,
				filesById,
				refetch,
				onDeleteFiles,
				handleOnAfterFileAction,
				onDownloadFiles,
				handleOnFolderDrop,
				sortBy,
				setSortBy,
				sortByDirection,
				setSortByDirection,
				selectColumns,
				canUpload,
				canUpdateFile,
				canDeleteFile,
			}}
		>
			<div className="flex flex-col shrink-0 gap-4 h-full">
				<div className="hidden md:flex flex-row justify-between items-center w-full">
					<FileExplorerSearch />
					<FileExplorerMenu
						dropzone={dropzone}
						uploadLoader={uploadLoading}
						isLoading={isLoading}
						menu={menu}
					/>
				</div>
				<FileExplorerWrapper dropzone={dropzone}>
					<FileExplorerHeader />
					{!filteredStructure.items.length ? (
						<div className="absolute inset-0">
							<BlankSlate
								withBorder={false}
								withBackground={false}
							/>
						</div>
					) : null}
					{filteredStructure.items.map((structureItem) => (
						<FileExplorerItemRow
							key={structureItem.id}
							structureItem={structureItem}
							level={0}
						/>
					))}
				</FileExplorerWrapper>
			</div>
		</FileExplorerContext.Provider>
	);
}
